import React, { useMemo } from 'react';

import { SLACircleIndicatorProps, SLAData, SLAStatus } from './SLACircleIndicator.props';
import { CircleContainer, CircleSvg, TimeText } from './SLACircleIndicator.styles';
import { COLORS } from '../../../configs/colors';
import {
  getSLAUrgency,
  getTerminalSLAUrgency,
  SLA_URGENCY_GRADIENTS,
} from '../../../utils/sla-urgency';

/**
 * Parse an SLA summary string into SLAData.
 * Handles both JSON (from detail endpoints) and plain comma-separated strings
 * from the service list API (e.g. "2026-01-30 10:30 , not breached").
 */
export const parseSLASummaryString = (summary: string): SLAData | null => {
  try {
    const parsed = JSON.parse(summary);
    if (parsed && typeof parsed === 'object') return parsed as SLAData;
  } catch {
    // Not JSON — fall through to plain string parsing
  }

  const dateMatch = summary.match(/^(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2})/);
  if (!dateMatch) return null;

  // Append seconds and Z to ensure UTC interpretation (backend stores UTC)
  const timeoutAt = dateMatch[1].replace(/\s+/, 'T') + ':00.000Z';
  const lower = summary.toLowerCase();
  const isBreached = lower.includes('breached') && !lower.includes('not breached');

  return {
    timeoutAt,
    slaOutcome: isBreached ? { outcomeTypeCode: 'breach' } : undefined,
  };
};

const calculateSLAFromSummary = (summary: string | null): SLAStatus => {
  if (!summary) {
    return { status: 'none', display: '\u2014', percentage: 0, color: COLORS.Gray };
  }

  const slaData = parseSLASummaryString(summary);
  const terminal = getTerminalSLAUrgency();

  if (!slaData || !slaData.timeoutAt) {
    // Check if the raw string indicates breach
    const lower = summary.toLowerCase();
    const isBreached = lower.includes('breached') && !lower.includes('not breached');
    if (isBreached) {
      return { status: 'breached', display: 'BREACH', percentage: 100, color: terminal.color };
    }
    return { status: 'none', display: '\u2014', percentage: 0, color: COLORS.Gray };
  }

  const outcome = slaData.slaOutcome?.outcomeTypeCode;

  if (outcome === 'breach') {
    return { status: 'breached', display: 'BREACH', percentage: 100, color: terminal.color };
  }

  if (outcome === 'complete') {
    return { status: 'complete', display: '\u2713', percentage: 100, color: terminal.color };
  }

  const timeout = new Date(slaData.timeoutAt);
  if (isNaN(timeout.getTime())) {
    return { status: 'none', display: '\u2014', percentage: 0, color: COLORS.Gray };
  }

  const now = new Date();
  const diffMs = timeout.getTime() - now.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 0) {
    return { status: 'breached', display: 'BREACH', percentage: 100, color: terminal.color };
  }

  const urgency = getSLAUrgency(diffMins);
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  const display = hours >= 1 ? (mins > 0 ? `${hours}h ${mins}m` : `${hours}h`) : `${diffMins}m`;

  return {
    status: `urgency-${urgency.level}`,
    display,
    percentage: Math.min(100, (diffMins / 2880) * 100),
    color: urgency.color,
  };
};

export const SLACircleIndicator: React.FC<SLACircleIndicatorProps> = ({
  clientSLASummary,
  opsSLASummary,
  size = 48,
  strokeWidth = 5,
}) => {
  const slaStatus = useMemo(() => {
    const summary = clientSLASummary || opsSLASummary || null;
    return calculateSLAFromSummary(summary);
  }, [clientSLASummary, opsSLASummary]);

  const isCritical = slaStatus.status === 'urgency-1';
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (slaStatus.percentage / 100) * circumference;
  const gradientId = `sla-grad-${slaStatus.status}`;
  const urgencyLevel = slaStatus.status.startsWith('urgency-')
    ? (parseInt(slaStatus.status.split('-')[1], 10) as 0 | 1 | 2 | 3 | 4 | 5 | 6)
    : slaStatus.status === 'breached' || slaStatus.status === 'complete'
      ? 0
      : null;
  const [gradStart, gradEnd] =
    urgencyLevel !== null ? SLA_URGENCY_GRADIENTS[urgencyLevel] : [COLORS.Gray, COLORS.Gray];

  return (
    <CircleContainer
      isCritical={isCritical}
      style={{ width: size, height: size }}
      data-testid="sla-circle"
    >
      <CircleSvg width={size} height={size} data-sla-status={slaStatus.status}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={gradStart} />
            <stop offset="100%" stopColor={gradEnd} />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e8edf2"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
        />
      </CircleSvg>
      <TimeText sx={isCritical ? { color: COLORS.SLABreached, fontWeight: 800 } : undefined}>
        {slaStatus.display}
      </TimeText>
    </CircleContainer>
  );
};
